import React, { useState } from 'react';
import { FileText, Trash2, Loader2, Upload, Cloud, ExternalLink, Info } from 'lucide-react';
import {
  getProcessingStatusColor,
  getProcessingStatusIcon,
  getProcessingStatusText,
} from '../utils/statusUtils';
import {
  Button,
  Badge,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  TooltipArrow,
  useAuth,
  kmsService,
} from '@aaas/shared';
import { Document } from '../types';
import { useDocumentListQuery } from '../hooks/useSpaceListQuery';
import { useListDriveIntegrations } from '../hooks/useDriveIntegrationQuery';

interface DocumentListProps {
  spaceId: string;
}

const DocumentList: React.FC<DocumentListProps> = ({ spaceId }) => {
  const { idToken } = useAuth();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState<Document | null>(null);
  const [isDeletingDocument, setIsDeletingDocument] = useState(false);
  const [sourceFilter, setSourceFilter] = useState<string>('all');

  // Fetch Google Drive connections for filter options
  const { data: driveConnectionsData } = useListDriveIntegrations({
    idToken: idToken || undefined,
    spaceId,
    enabled: !!(spaceId && idToken),
  });

  const driveConnections = driveConnectionsData?.data?.items || [];

  // Fetch documents using React Query
  const {
    data: documentsResponse,
    isLoading: loading,
    error,
    refetch,
  } = useDocumentListQuery({
    spaceId,
    idToken: idToken || undefined,
    enabled: !!(spaceId && idToken),
    sourceFilter: sourceFilter === 'all' ? undefined : sourceFilter,
  });

  const documents = documentsResponse?.data || [];

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatFileSize = (bytes: number | undefined) => {
    if (!bytes) return 'Unknown';
    const mb = bytes / (1024 * 1024);
    return `${mb.toFixed(2)} MB`;
  };

  const getSourceDisplay = (document: Document) => {
    const source = document.external_source;
    const sourceUrl = document.external_source_url;
    const connectionName = document.connection_name;

    if (!source) {
      return {
        label: 'Upload',
        icon: <Upload className="h-3 w-3" />,
        isClickable: false,
        url: null,
        color: 'text-gray-600',
      };
    }

    switch (source) {
      case 'google_drive':
        return {
          label: connectionName || 'Google Drive',
          icon: <Cloud className="h-4 w-4" />,
          isClickable: false,
          url: null,
          color: 'text-blue-600',
        };
      default:
        return {
          label: source,
          icon: <Cloud className="h-4 w-4" />,
          isClickable: !!sourceUrl,
          url: sourceUrl,
          color: 'text-gray-600',
        };
    }
  };

  const handleDeleteDocument = async () => {
    if (!documentToDelete || !idToken) return;

    setIsDeletingDocument(true);
    try {
      await kmsService.deleteDocument(idToken, spaceId, documentToDelete.id);

      // Refetch the documents list to reflect the deletion
      refetch();
      setShowDeleteDialog(false);
      setDocumentToDelete(null);
    } catch (err) {
      // Handle delete error gracefully
      console.error('Failed to delete document:', err);
      alert('Failed to delete document');
    } finally {
      setIsDeletingDocument(false);
    }
  };

  const openDeleteDialog = (document: Document) => {
    setDocumentToDelete(document);
    setShowDeleteDialog(true);
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Documents</CardTitle>
          <CardDescription>Documents uploaded to this space</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center h-32">
            <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Documents</CardTitle>
          <CardDescription>Documents uploaded to this space</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col items-center justify-center h-32 text-red-500">
            <p className="text-sm">Failed to load documents</p>
            <Button variant="outline" size="sm" onClick={() => refetch()} className="mt-2">
              Try Again
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Determine if we should show the filter
  // Show filter if: there are documents, a filter is applied, or there are Google Drive connections
  const shouldShowFilter =
    documents.length > 0 || sourceFilter !== 'all' || driveConnections.length > 0;

  return (
    <TooltipProvider>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Documents</CardTitle>
              <CardDescription>
                {documents.length} document{documents.length !== 1 ? 's' : ''} uploaded to this
                space
              </CardDescription>
            </div>

            {/* Source Filter */}
            {shouldShowFilter && (
              <div className="flex items-center gap-2 mt-4">
                <Select value={sourceFilter} onValueChange={setSourceFilter}>
                  <SelectTrigger className="w-[220px]">
                    <SelectValue placeholder="Filter by source" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Sources</SelectItem>
                    <SelectItem value="upload">Manual Upload</SelectItem>
                    {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
                    {driveConnections.map((connection: any) => (
                      <SelectItem key={connection.id} value={connection.id}>
                        {connection.resource_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {documents.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-32 text-gray-500 p-6">
              <FileText className="h-12 w-12 text-gray-300 mb-2" />
              {sourceFilter === 'all' ? (
                <>
                  <p className="text-sm">No documents uploaded yet</p>
                  <p className="text-xs text-gray-400 mt-1">
                    Click "Upload Documents" to add files
                  </p>
                </>
              ) : (
                <>
                  <p className="text-sm">No documents found for this filter</p>
                  <p className="text-xs text-gray-400 mt-1">Try selecting a different source</p>
                </>
              )}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Document Name</TableHead>
                  <TableHead>Source</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Size</TableHead>
                  <TableHead>Chunks</TableHead>
                  <TableHead>Uploaded</TableHead>
                  <TableHead className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <span>Actions</span>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="inline-flex">
                            <Info className="h-4 w-4 text-blue-500 cursor-help" />
                          </span>
                        </TooltipTrigger>
                        <TooltipContent className="max-w-xs">
                          <TooltipArrow className="fill-popover" />
                          <div className="space-y-1">
                            <p className="font-medium">Google Drive synced documents</p>
                            <p className="text-xs text-muted-foreground">
                              To delete, remove the file from Google Drive and sync again.
                            </p>
                          </div>
                        </TooltipContent>
                      </Tooltip>
                    </div>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {documents.map((document) => {
                  const sourceInfo = getSourceDisplay(document);

                  return (
                    <TableRow key={document.id}>
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-2">
                          <FileText className="h-4 w-4 text-gray-400" />
                          <span>{document.name}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        {sourceInfo.isClickable && sourceInfo.url ? (
                          <a
                            href={sourceInfo.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={`flex items-center gap-1.5 ${sourceInfo.color} hover:underline w-fit`}
                            title={`View in ${sourceInfo.label}`}
                          >
                            {sourceInfo.icon}
                            <span className="text-sm">{sourceInfo.label}</span>
                            <ExternalLink className="h-3 w-3" />
                          </a>
                        ) : (
                          <div className={`flex items-center gap-2 ${sourceInfo.color}`}>
                            {sourceInfo.icon}
                            <span className="text-sm">{sourceInfo.label}</span>
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant={null as any}
                          className={`${getProcessingStatusColor(document.upload_status)} flex items-center gap-1 w-fit border-transparent`}
                        >
                          {getProcessingStatusIcon(document.upload_status)}
                          {getProcessingStatusText(document.upload_status)}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm text-gray-600">
                        {formatFileSize(document.file_size)}
                      </TableCell>
                      <TableCell className="text-sm text-gray-600">
                        {document.upload_status === 'ready' && document.chunk_count
                          ? `${document.chunk_count} chunks`
                          : document.upload_status === 'processing'
                            ? 'Processing...'
                            : '-'}
                      </TableCell>
                      <TableCell className="text-sm text-gray-600">
                        {formatDate(document.created_at)}
                      </TableCell>
                      <TableCell className="text-right">
                        {document.external_source === 'google_drive' && document.connection_name ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            disabled
                            className="h-8 px-2 text-gray-400 cursor-not-allowed"
                          >
                            <Trash2 className="h-4 w-4 mr-1" />
                            Delete
                          </Button>
                        ) : (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openDeleteDialog(document)}
                            disabled={isDeletingDocument && documentToDelete?.id === document.id}
                            className="h-8 px-2 text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            {isDeletingDocument && documentToDelete?.id === document.id ? (
                              <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                            ) : (
                              <Trash2 className="h-4 w-4 mr-1" />
                            )}
                            {isDeletingDocument && documentToDelete?.id === document.id
                              ? 'Deleting...'
                              : 'Delete'}
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Delete Document Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Document</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{documentToDelete?.name}"? This action cannot be
              undone. All document data and processing results will be permanently removed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeletingDocument}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteDocument}
              disabled={isDeletingDocument}
              className="bg-red-600 hover:bg-red-700"
            >
              {isDeletingDocument ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                'Delete Document'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </TooltipProvider>
  );
};

export default DocumentList;
